
import Vue from "vue";
import { format } from "date-fns";
import { Plan, Version } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import ChevronDownIcon from "icons/ChevronDown.vue";
import { CodeDiff } from "v-code-diff";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ChevronDownIcon, CodeDiff },
  props: {
    memoId: {
      type: String,
      required: true,
    },
    content: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      openedDates: [] as string[],
      previewVersionId: "",
      previewContent: "",
      existsDiff: true,
      isLoading: true,
    };
  },
  async mounted() {
    await this.$store.dispatch("memoModule/fetchMemoVersions", this.memoId);
    this.isLoading = false;
    if (this.versionDates.length > 0) {
      this.openedDates.push(this.versionDates[0]);
    }
  },
  methods: {
    async onClickVersion(versionId: string) {
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t("memoVersionsDialog.confirm.title") as string,
        content: this.$t("memoVersionsDialog.confirm.content") as string,
        change: this.$t("memoVersionsDialog.confirm.restore") as string,
      };
      const result = await confirmDialog.show(data);

      if (!result) return;

      const res = await this.$store.dispatch("memoModule/restoreMemoVersion", {
        memoId: this.memoId,
        versionId,
      });

      this.$close(true);

      if (!res) {
        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: this.$t("versionsDialog.error.title") as string,
          content: this.$t("versionsDialog.error.content") as string,
          isError: true,
        };
        errorDialog.show(errorData);
        return;
      }

      const doneDialog = new Dialog(SimpleDialog);
      const doneData: SimpleDialogProps = {
        title: this.$t("versionsDialog.complete.title") as string,
        content: this.$t("memoVersionsDialog.restoreCompleteContent") as string,
      };
      doneDialog.show(doneData);
    },
    async onClickPreview(versionId: string) {
      const previewContent = await this.$store.dispatch("memoModule/fetchMemoVersionContent", {
        memoId: this.memoId,
        versionId,
      });
      this.previewContent = previewContent ?? "";
      this.previewVersionId = versionId;
    },
    onClickBack() {
      this.previewContent = "";
      this.previewVersionId = "";
      this.existsDiff = true;
    },
    onClickOutSide() {
      this.$close(true);
    },
    onClickSubscription() {
      this.$router.push({ name: "subscriptionAnnounce" });
      this.$close(true);
    },
    onToggleAccordion(date: string) {
      if (this.openedDates.includes(date)) {
        this.openedDates = this.openedDates.filter((d) => d !== date);
      } else {
        this.openedDates.push(date);
      }
    },
    afterRenderDiff() {
      const existsDiff = document.querySelector(".d2h-file-wrapper");
      if (!existsDiff) this.existsDiff = false;
    },
  },
  computed: {
    versions(): Version[] {
      return this.$store.getters["memoModule/memoVersions"](this.memoId) ?? [];
    },
    versionDates(): string[] {
      const v = this.versions;
      if (!v.length) return [];
      const set = v.reduce(
        (acc, cur) => acc.add(cur.lastModified.slice(0, 10)),
        new Set<string>()
      );
      return [...set];
    },
    versionsByDate() {
      return (date: string) =>
        this.versions.filter((version) => version.lastModified.startsWith(date));
    },
    lastModifiedTime() {
      return (lastModified: string) =>
        format(lastModified, this.$t("versionsDialog.dateFormat.date") as string);
    },
    lastModifiedDate() {
      return (lastModified: string) =>
        format(lastModified, this.$t("versionsDialog.dateFormat.time") as string);
    },
    openedDate() {
      return (date: string) => this.openedDates.includes(date);
    },
    plan() {
      return this.$store.getters["billingModule/plan"];
    },
    isFree(): boolean {
      return this.plan === Plan.free;
    },
  },
});

interface Props {
  memoId: string;
  content: string;
}

interface Data {
  openedDates: string[];
  previewVersionId: string;
  previewContent: string;
  existsDiff: boolean;
  isLoading: boolean;
}

interface Methods {
  onClickOutSide(): void;
  onClickSubscription(): void;
  onClickVersion(versionId: string): void;
  onClickPreview(versionId: string): void;
  onClickBack(): void;
  onToggleAccordion(date: string): void;
  afterRenderDiff(): void;
}

interface Computed {
  versions: Version[];
  versionDates: string[];
  versionsByDate(date: string): Version[];
  lastModifiedTime(lastModified: string): string;
  lastModifiedDate(lastModified: string): string;
  openedDate(date: string): boolean;
  plan: Plan;
  isFree: boolean;
}

export type MemoVersionsDialogProps = Props;
